import React from 'react';
import { assessmnetsApi, Side, TraineeAssessmentsDTO } from '../api';

// countdown to a target datetime (ISO string). If target is past, returns ''.
function useCountdown(target?: string | null) {
  const [remaining, setRemaining] = React.useState<string>('');
  React.useEffect(() => {
    if (!target) { setRemaining(''); return; }
    const targetMs = new Date(target).getTime();
    const id = setInterval(() => {
      const diff = Math.max(0, targetMs - Date.now());
      if (diff <= 0) { setRemaining(''); return; }
      const hh = Math.floor(diff / 3600000);
      const mm = Math.floor((diff % 3600000) / 60000);
      const ss = Math.floor((diff % 60000) / 1000);
      setRemaining(hh > 0 ? `${hh}h ${mm}m ${ss}s` : `${mm}m ${ss}s`);
    }, 1000);
    return () => clearInterval(id);
  }, [target]);
  return remaining;
}

function fmt(d?: string | null) {
  if (!d) return '—';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

export default function AssessmentsPage() {
  const [data, setData] = React.useState<TraineeAssessmentsDTO | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [starting, setStarting] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const response = await assessmnetsApi.getTraineeAssessments();
        if (response.success && response.data) setData(response.data);
      } catch (e: any) {
        setError(e?.response?.data?.message || e?.message || 'Failed to load assessments');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onStart = async (batchId: number, side: Side) => {
    console.log(`Starting assessment for batch ${batchId}, side ${side}`);
    setStarting(`${batchId}-${side}`);
    try {
      const response = await assessmnetsApi.startAssessment(batchId, side);
      if (response.success && response.data) {
        // Log the launch URL before opening a new tab for visibility/debugging
        console.log('Assessment launch URL:', response.data.launch_url);
        window.open(response.data.launch_url, '_blank', 'noopener,noreferrer');
      }
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to start assessment');
    } finally {
      setStarting(null);
    }
  };

  if (loading) return <div className="p-6">Loading assessments…</div>;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return null;

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold">My Assessments</h1>

      {data.batch_assessments.length === 0 && (
        <div className="text-gray-500">No batches assigned.</div>
      )}

      <div className="space-y-4">
        {data.batch_assessments.map((item, idx) => (
          <div key={idx} className="rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">{item.batch.batch_code}</div>
                <div className="text-sm text-gray-600">{item.batch.program?.name}</div>
              </div>
              <div className="text-sm text-gray-500">
                Attendance: {String(item.attendance_percentage)}%
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <AssessmentCard
                title="Pre-assessment"
                startAt={item.batch.pre_assessment_start_datetime}
                endAt={item.batch.pre_assessment_end_datetime}
                state={item.computed.pre}
                batchId={item.batch.batch_id}
                side="pre"
                startingKey={starting}
                onStart={onStart}
              />
              <AssessmentCard
                title="Post-assessment"
                startAt={item.batch.post_assessment_datetime}
                endAt={item.batch.post_assessment_end_datetime}
                state={item.computed.post}
                batchId={item.batch.batch_id}
                side="post"
                startingKey={starting}
                onStart={onStart}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AssessmentCard({
  title,
  startAt,
  endAt,
  state,
  batchId,
  side,
  startingKey,
  onStart,
}: {
  title: string;
  startAt?: string | null;
  endAt?: string | null;
  state: { open: boolean; locked: boolean; taken: boolean; assigned: boolean };
  batchId: number;
  side: Side;
  startingKey: string | null;
  onStart: (batchId: number, side: Side) => void;
}) {
  // 10-min early window is already applied server-side; here we show basic times.
  const opensIn = useCountdown(startAt);
  const closesIn = useCountdown(endAt);
  const starting = startingKey === `${batchId}-${side}`;

  return (

    <div className="rounded-lg border p-4">
      {/* DEBUGGING INFO START */}
      {/* <div className="bg-red-50 text-red-800 text-xs p-2 rounded mb-2">
                    Raw StartAt: {startAt}
                    <br />
                    Countdown Value: {opensIn}
                </div> */}
      {/* DEBUGGING INFO END */}
      <div className="flex items-center justify-between">
        <div className="font-medium">{title}</div>
        <div className="text-xs text-gray-500 text-right">
          <div>Starts: {fmt(startAt)}</div>
          <div>Ends: {fmt(endAt)}</div>
        </div>
      </div>

      {state.taken ? (
        <div className="mt-3 text-sm text-gray-600">
          Assessment completed.
        </div>
      ) : state.assigned ? (
        state.open ? (
          <div className="mt-3">
            <button
              onClick={() => onStart(batchId, side)}
              disabled={starting}
              className="px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {starting ? 'Starting…' : 'Start assessment'}
            </button>
            <p className="text-xs text-gray-500 mt-2">
              A new tab will open. After completion, we will record your attempt automatically.
            </p>
            {endAt && (
              <p className="text-xs text-gray-500 mt-1">
                Closes in {closesIn || 'soon'}.
              </p>
            )}
          </div>
        ) : (
          <div className="mt-3 text-sm text-gray-600">
            {state.locked ? (
              <>Assessment window has closed.</>
            ) : startAt ? (
              <>Not open yet. Opens in {opensIn || 'a while'}.</>
            ) : (
              <>Assessment schedule is not available yet.</>
            )}
          </div>
        )
      ) : (
        <div className="mt-3 text-sm text-gray-500">Not assigned.</div>
      )}
    </div>
  );
}
